import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import cx from 'classnames';

import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { Grid, units } from '@ha/dsl';

import { getIsAdmin, getIsAdvertiser } from 'ha/modules/AuthLogic/selectors';
import SearchDateFilterNext from './SearchFilters/SearchDateFilterNext';
import { SearchAlert } from './SearchAlerts/SearchAlert';
import SortingButton from './SortingButton';
import SearchMapViewControls from './SearchMapViewControls';
import SearchResultsInfoBar from './SearchResultsInfoBar';
import ToggleMobileMap from './ToggleMobileMap';
import SearchFiltersButton from './SearchFilters/SearchFiltersButton';
import { MapViewState } from '../utils';
import {
  getIsAdvertiserSearch,
  getIsSearchEmpty,
  getMapViewState,
} from '../selectors';

const useStyles = makeStyles(theme => ({
  searchLocationTitleMobile: {
    margin: units.trbl(0, 0.7, 1, 0.7),
  },
  buttonContainer: {
    flexShrink: 0,
    marginRight: units.rem(0.5),
    '&:last-child': {
      marginRight: 0,
    },
  },
  filtersBar: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    backgroundColor: theme.palette.white.main,
    [theme.breakpoints.up('md')]: {
      top: `${theme.customSpacing.navHeightLg}px`,
    },
  },
  controlsContainer: {
    overflowX: 'auto',
    margin: units.yx(1, 0.7),
    [theme.breakpoints.up('md')]: {
      margin: units.yx(1, 0),
    },
  },
  controlsContainerSecondary: { marginTop: 0 },
  controlsContainerDesktop: {
    margin: 0,
    height: '68px',
  },
  marginLeftAuto: {
    marginLeft: 'auto',
  },
}));

export const SearchOverview: React.FC = () => {
  const classes = useStyles();
  const { md } = useMediaQuery();

  const isAdvertiserSearch = useSelector(getIsAdvertiserSearch);
  const isAdvertiser = useSelector(getIsAdvertiser);
  const isAdmin = useSelector(getIsAdmin);
  const isSearchEmpty = useSelector(getIsSearchEmpty);
  const mapViewState = useSelector(getMapViewState);

  return (
    <React.Fragment>
      <Grid container className={classes.filtersBar}>
        <Grid
          container
          data-test-locator="Search Overview Filters"
          className={cx(
            classes.controlsContainer,
            md && classes.controlsContainerDesktop,
          )}
          wrap="nowrap"
          alignItems="center"
        >
          {md && (
            <Grid item zeroMinWidth>
              <SearchResultsInfoBar />
            </Grid>
          )}

          <Grid
            item
            className={cx(
              classes.buttonContainer,
              md && classes.marginLeftAuto,
            )}
          >
            <SortingButton
              size={md ? 'medium' : 'small'}
              disabled={md && mapViewState === MapViewState.fullMap}
            />
          </Grid>

          {!md && (
            <React.Fragment>
              <Grid item className={classes.buttonContainer}>
                <SearchDateFilterNext />
              </Grid>
              <Grid item className={classes.buttonContainer}>
                <SearchFiltersButton buttonSize={md ? 'medium' : 'small'} />
              </Grid>
            </React.Fragment>
          )}
          {md && (
            <Grid item className={classes.buttonContainer}>
              <SearchMapViewControls />
            </Grid>
          )}
        </Grid>
      </Grid>
      {!md && (
        <Grid container>
          <Grid
            container
            className={cx(
              classes.controlsContainer,
              classes.controlsContainerSecondary,
            )}
            wrap="nowrap"
          >
            <Grid item className={classes.buttonContainer}>
              <ToggleMobileMap color="secondary" />
            </Grid>
            {(!isAdvertiser || isAdmin) &&
              !isAdvertiserSearch &&
              !isSearchEmpty && (
                <Grid item className={classes.buttonContainer}>
                  <SearchAlert />
                </Grid>
              )}
          </Grid>
        </Grid>
      )}
      {!md && (
        <div className={classes.searchLocationTitleMobile}>
          <SearchResultsInfoBar />
        </div>
      )}
    </React.Fragment>
  );
};

export default SearchOverview;
